import { useEffect, useRef, useState, type ReactNode } from 'react';
import Page from 'src/components/Page';

/** Page with an opt-in content column. `lg` is 1800px; the default is 1440px.
 *  `fixedHeight` and `fill` occupy the space under the page header. The layout
 *  shell scrolls when a page is taller than that space.
 */
export default function PageFrame({
  title,
  children,
  className = '',
  bgColor,
  size = 'md',
  fixedHeight = false,
  fill = false
}: {
  title: string;
  children: ReactNode;
  className?: string;
  bgColor?: string;
  size?: 'md' | 'lg';
  fixedHeight?: boolean;
  /** Fill the space under the page header. For viewers that must not scroll the window. */
  fill?: boolean;
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const maxWidth = size === 'lg' ? 'max-w-[1800px]' : 'max-w-[1440px]';
  const frame = fixedHeight
    ? 'relative flex h-full min-h-0 w-full flex-col pl-4 md:pl-8'
    : fill
      ? `mx-auto flex h-full min-h-0 w-full flex-1 flex-col px-4 md:px-8 ${maxWidth}`
      : `mx-auto w-full min-w-0 px-4 pb-4 pt-4 md:px-8 md:pt-0 ${maxWidth}`;

  useEffect(() => {
    const frameEl = frameRef.current;
    if (!fixedHeight || !frameEl) return;

    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || target === frameEl) return;
      if (!target.classList.contains('chat-conversation-area')) return;
      // The canvas document has its own taller scroller; the page edge is for chat.
      if (target.closest('.editor-content-wrapper')) return;
      // Ignore nested widgets and sub-pixel overflow. A short chat auto-scrolls
      // to the bottom of its padding, which is not the user leaving the top.
      const overflow = target.scrollHeight - target.clientHeight;
      const moved = overflow >= 8 && target.scrollTop > 8;
      if (scrollerRef.current === target) {
        if (!moved) {
          scrollerRef.current = null;
          setScrolled(false);
        }
        return;
      }
      if (!moved) return;
      const current = scrollerRef.current;
      if (current?.isConnected && current.scrollHeight >= target.scrollHeight) return;
      scrollerRef.current = target;
      setScrolled(true);
    };

    frameEl.addEventListener('scroll', onScroll, true);
    // Leaving a chat unmounts the conversation without a scroll event, which
    // left the edge visible on the new-chat screen.
    const observer = new MutationObserver(() => {
      const area = frameEl.querySelector('.chat-conversation-area');
      if (!(area instanceof HTMLElement)) {
        scrollerRef.current = null;
        setScrolled(false);
        return;
      }
      const overflow = area.scrollHeight - area.clientHeight;
      if (overflow < 8 || area.scrollTop <= 8) {
        scrollerRef.current = null;
        setScrolled(false);
      }
    });
    observer.observe(frameEl, { childList: true, subtree: true });
    return () => {
      frameEl.removeEventListener('scroll', onScroll, true);
      observer.disconnect();
    };
  }, [fixedHeight]);

  return (
    <Page title={title} bgColor={bgColor}>
      <div ref={frameRef} className={`${frame} ${className}`}>
        {fixedHeight ? (
          <div
            aria-hidden
            className={`pointer-events-none absolute -left-4 right-0 top-0 z-10 h-px bg-border-subtle transition-opacity md:-left-8 ${
              scrolled ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ) : null}
        {children}
      </div>
    </Page>
  );
}
